// VOY-416: fire-and-forget trigger for the Claude profile intelligence agent.
//
// The canonical "right" place for these triggers is the backend, but several
// profile-mutating writes (quiz_results insert, user_preferences keywords
// upsert) currently go FE → Supabase direct, bypassing any backend hook. Until
// those writes migrate to backend endpoints, we call the agent from the FE.
//
// The call is fire-and-forget — the agent runs async server-side and the UI
// must not block or fail on its outcome. Deliberately NOT using apiFetch()
// here because apiFetch() toasts on error (src/lib/api.ts) — that would leak
// agent failures into the UX when a quiz save succeeded. Errors are logged
// to console only.

import { supabase } from './supabase'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

export type AgentTriggerEvent =
  // HTTP-allowed events (the backend whitelists these on POST).
  | 'quiz_completed'
  | 'preference_changed'
  // Server-side only. Exported for the type union so backend authors
  // writing triggerAnalysis() calls import a shared shape, but the FE
  // never sends this (the backend's HTTP handler rejects it with 403).
  | 'booking_confirmed'

export function triggerAgentAnalysis(
  userId: string,
  triggerEvent: AgentTriggerEvent,
  triggerData: Record<string, unknown> | null = null,
): void {
  if (!userId) return

  // Build the request without awaiting: the UI path above us must not block.
  void (async () => {
    try {
      const session = (await supabase.auth.getSession()).data.session
      const token = session?.access_token
      if (!token) {
        // No session = not logged in = nothing sensible to analyze. Skip
        // silently rather than firing an unauthenticated request.
        return
      }
      const res = await fetch(`${API_URL}/api/agent/analyze-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ userId, triggerEvent, triggerData }),
      })
      if (!res.ok) {
        const body = await res.text().catch(() => '')
        console.warn('[agent-trigger] non-ok response', {
          triggerEvent,
          status: res.status,
          body: body.slice(0, 200),
        })
      }
    } catch (err) {
      console.warn('[agent-trigger] failed', {
        triggerEvent,
        error: String(err),
      })
    }
  })()
}
